/** Keep only absolute http(s) citation URLs (matches rag-backend validation). */
export function filterHttpSources(urls: string[] | undefined): string[] {
  if (!urls?.length) return []
  return urls.filter(
    (url) => typeof url === "string" && /^https?:\/\//i.test(url.trim())
  )
}

/** Encode one part of a text-fragment directive. encodeURIComponent leaves "-"
 * unescaped, but "-" is the prefix/suffix delimiter in the directive grammar, so
 * encode it too. */
function encodeFragmentPart(text: string): string {
  return encodeURIComponent(text).replace(/-/g, "%2D")
}

/**
 * Append a Text Fragment (`#:~:text=…`) so clicking the link scrolls to and
 * highlights the cited passage on the source page. Best-effort: supported in
 * Chromium and Safari (ignored elsewhere), and only matches when the snippet
 * appears verbatim on the rendered page — otherwise the page just opens normally.
 *
 * For longer snippets we use the `textStart,textEnd` form (first/last few words)
 * rather than encoding the whole passage: it keeps the URL short and is more
 * robust to minor mid-passage differences between our stored text and the page.
 */
export function sourceUrlWithHighlight(
  url: string,
  snippet: string | undefined
): string {
  const text = (snippet ?? "").trim()
  if (!url || !text) return url

  const words = text.split(/\s+/)
  const directive =
    words.length > 12
      ? `text=${encodeFragmentPart(words.slice(0, 6).join(" "))},${encodeFragmentPart(
          words.slice(-6).join(" ")
        )}`
      : `text=${encodeFragmentPart(text)}`

  // Place the directive after any existing fragment, replacing a prior one.
  const hashIndex = url.indexOf("#")
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url
  const existing = hashIndex >= 0 ? url.slice(hashIndex + 1).split(":~:")[0] : ""
  return `${base}#${existing}:~:${directive}`
}
